// features/workstations/qqq/prediction/index.js
// ═══════════════════════════════════════════════════════════
// PREDICTION tab — live intraday prediction simulator
//
// Per Tim 2026-05-25: a place to run predictions live, see the
// 11:00 and 11:30 checkpoint outputs side-by-side, and iterate
// on the architecture without leaving the workstation.
//
// Backend: /api/qqq/intraday-prediction
// Baseline: 57.0% top-1, 83.5% top-3 (validated N=200 backtest)
//           85% hit rate when top-1 conf ≥ 50%
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import RoadmapSidebar from './roadmap-sidebar.js';
import HybridPanel from './hybrid-panel.js';
import F15Panel from './f15-panel.js';

const CHECKPOINTS = ['10:30', '11:00', '11:30', '14:00'];
const BASELINE = {
  '10:30': '46.5% / 79.5%',
  '11:00': '51.0% / 84.5%',
  '11:30': '57.0% / 83.5%',
  '14:00': '58.8% / 83.9%',
};

function todayISO() {
  // ET date
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

function RecommendationBadge({ recommendation, confidence }) {
  const toneByRec = {
    'TRADE': '#c8a96e',                  // gold — high conviction
    'TRADE-DIRECTIONAL': '#7a9e82',      // sage — directional bias
    'SMALL': '#b8956a',                  // sand — partial signal
    'SKIP': '#888',                      // gray — no signal
  };
  const color = toneByRec[recommendation] || '#888';
  return (
    <div style={{
      display: 'inline-block',
      padding: '6px 16px',
      borderRadius: 6,
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${color}66`,
      color,
      fontFamily: 'ui-monospace, monospace',
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: 0.5,
    }}>
      {recommendation}
      <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 11, fontWeight: 400 }}>
        {confidence}
      </span>
    </div>
  );
}

function PredictionCard({ result, checkpoint, onRun, loading }) {
  if (loading) {
    return (
      <div className="qqq-pred-card">
        <div className="qqq-pred-card__header">
          <span className="qqq-pred-card__cp">{checkpoint} ET</span>
          <span className="qqq-pred-card__baseline">baseline {BASELINE[checkpoint]}</span>
        </div>
        <div className="qqq-pred-card__loading">running prediction…</div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="qqq-pred-card">
        <div className="qqq-pred-card__header">
          <span className="qqq-pred-card__cp">{checkpoint} ET</span>
          <span className="qqq-pred-card__baseline">baseline {BASELINE[checkpoint]}</span>
        </div>
        <div className="qqq-pred-card__empty">
          <button onClick={onRun} className="qqq-pred-card__run">
            Run prediction at {checkpoint} ET
          </button>
        </div>
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="qqq-pred-card">
        <div className="qqq-pred-card__header">
          <span className="qqq-pred-card__cp">{checkpoint} ET</span>
        </div>
        <div className="qqq-pred-card__error">
          <p>Error: {result.error}</p>
          {result.suggestion && <p style={{ opacity: 0.6, fontSize: 12, marginTop: 8 }}>{result.suggestion}</p>}
          <button onClick={onRun} className="qqq-pred-card__run" style={{ marginTop: 12 }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { prediction, recommendation, intradayVars, transitionPrior, yesterday } = result;
  const weights = Object.entries(prediction.weights || {}).sort((a, b) => b[1] - a[1]);
  const top3 = weights.slice(0, 3);
  const dir = recommendation.directionalSignal;

  return (
    <div className="qqq-pred-card">
      <div className="qqq-pred-card__header">
        <span className="qqq-pred-card__cp">{checkpoint} ET</span>
        <span className="qqq-pred-card__baseline">baseline {BASELINE[checkpoint]}</span>
        <button onClick={onRun} className="qqq-pred-card__refresh" title="Re-run prediction">
          ↻
        </button>
      </div>

      <div className="qqq-pred-card__rec">
        <RecommendationBadge recommendation={recommendation.recommendation} confidence={recommendation.confidence} />
        <span className="qqq-pred-card__rec-shape">
          {prediction.top1} <span style={{ opacity: 0.6 }}>@ {(recommendation.top1Weight * 100).toFixed(1)}%</span>
        </span>
      </div>

      <div className="qqq-pred-card__directional">
        <div className="qqq-pred-card__dir-label">Direction (the trade-decision question)</div>
        <div className="qqq-pred-card__dir-bars">
          <div className="qqq-pred-card__dir-row">
            <span>Up</span>
            <div className="qqq-pred-card__dir-bar"><div style={{ width: `${(dir.distribution.up * 100).toFixed(0)}%`, background: '#7a9e82' }} /></div>
            <span>{(dir.distribution.up * 100).toFixed(0)}%</span>
          </div>
          <div className="qqq-pred-card__dir-row">
            <span>Down</span>
            <div className="qqq-pred-card__dir-bar"><div style={{ width: `${(dir.distribution.down * 100).toFixed(0)}%`, background: '#c46050' }} /></div>
            <span>{(dir.distribution.down * 100).toFixed(0)}%</span>
          </div>
          <div className="qqq-pred-card__dir-row">
            <span>Chop</span>
            <div className="qqq-pred-card__dir-bar"><div style={{ width: `${(dir.distribution.chop * 100).toFixed(0)}%`, background: '#6b8fa8' }} /></div>
            <span>{(dir.distribution.chop * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="qqq-pred-card__top3">
        <div className="qqq-pred-card__dir-label">Top-3 scenarios (83.5% of days contain actual)</div>
        {top3.map(([shape, w]) => (
          <div key={shape} className="qqq-pred-card__top3-row">
            <span>{shape}</span>
            <div className="qqq-pred-card__top3-bar"><div style={{ width: `${(w * 100).toFixed(0)}%`, background: '#c8a96e' }} /></div>
            <span>{(w * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>

      <div className="qqq-pred-card__reasoning">
        <div className="qqq-pred-card__dir-label">Reasoning</div>
        <p>{prediction.reasoning}</p>
      </div>

      <details className="qqq-pred-card__details">
        <summary>Intraday variables observed</summary>
        <ul>
          <li>Gap: ${intradayVars.gap?.toFixed(2)} ({intradayVars.gapPct?.toFixed(2)}%)</li>
          <li>F15 high/low: {intradayVars.f15High?.toFixed(2)} / {intradayVars.f15Low?.toFixed(2)} (close {intradayVars.f15ClosePosBucket})</li>
          <li>F15 vol ratio: {intradayVars.f15VolumeRatio?.toFixed(2)}x</li>
          <li>Drive: {intradayVars.openingDriveDirection} (${intradayVars.driveMagnitude?.toFixed(2)})</li>
          <li>Pullback: {(intradayVars.pullbackDepth * 100).toFixed(0)}% ({intradayVars.pullbackDepthBucket})</li>
          <li>Position vs F15: {intradayVars.positionVsF15_now}</li>
          <li>Range/net @ checkpoint: {intradayVars.rangeNetRatioNow?.toFixed(1)}x</li>
        </ul>
      </details>

      <details className="qqq-pred-card__details">
        <summary>Context (yesterday + transition prior)</summary>
        <p>Yesterday: <strong>{yesterday.label}</strong> ({yesterday.date})</p>
        {transitionPrior && (
          <>
            <p style={{ opacity: 0.6, fontSize: 12 }}>
              Historical transitions from {yesterday.label} (N={transitionPrior.total}):
            </p>
            <ul>
              {transitionPrior.rows.slice(0, 5).map((r) => (
                <li key={r.label}>{(r.pct * 100).toFixed(0)}% {r.label}</li>
              ))}
            </ul>
          </>
        )}
      </details>
    </div>
  );
}

export default function QqqPredictionTab({ workstation, userId, userRecord, resolvedAiName }) {
  const [date, setDate] = useState(todayISO());
  const [results, setResults] = useState({});  // { '11:00': result, '11:30': result }
  const [loading, setLoading] = useState({});

  const runPrediction = useCallback(async (checkpoint) => {
    setLoading((l) => ({ ...l, [checkpoint]: true }));
    try {
      const res = await fetch(`/api/qqq/intraday-prediction?date=${date}&checkpoint=${checkpoint}`);
      const data = await res.json();
      setResults((r) => ({ ...r, [checkpoint]: data }));
    } catch (err) {
      setResults((r) => ({ ...r, [checkpoint]: { error: err.message } }));
    } finally {
      setLoading((l) => ({ ...l, [checkpoint]: false }));
    }
  }, [date]);

  // Reset results when date changes
  useEffect(() => { setResults({}); }, [date]);

  return (
    <div className="qqq-prediction-tab">
      <RoadmapSidebar />
      <style jsx>{`
        .qqq-prediction-tab {
          padding: 16px;
          color: #ccc;
          font-family: -apple-system, sans-serif;
        }
        .qqq-pred-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #272736;
        }
        .qqq-pred-header h2 {
          margin: 0;
          font-size: 16px;
          color: #c8a96e;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .qqq-pred-header__subtitle {
          font-size: 12px;
          color: #888;
        }
        .qqq-pred-date {
          background: #171723;
          border: 1px solid #363650;
          color: #ccc;
          padding: 6px 10px;
          border-radius: 4px;
          font-family: ui-monospace, monospace;
          font-size: 13px;
        }
        .qqq-pred-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 24px;
        }
        :global(.qqq-pred-card) {
          background: #111118;
          border: 1px solid #272736;
          border-radius: 8px;
          padding: 16px;
        }
        :global(.qqq-pred-card__header) {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #1e1e2a;
        }
        :global(.qqq-pred-card__cp) {
          font-family: ui-monospace, monospace;
          font-size: 14px;
          color: #c8a96e;
          font-weight: 600;
        }
        :global(.qqq-pred-card__baseline) {
          font-size: 11px;
          color: #888;
        }
        :global(.qqq-pred-card__refresh) {
          margin-left: auto;
          background: transparent;
          border: 1px solid #272736;
          color: #888;
          border-radius: 4px;
          width: 24px;
          height: 24px;
          cursor: pointer;
        }
        :global(.qqq-pred-card__refresh:hover) {
          color: #c8a96e;
          border-color: #c8a96e44;
        }
        :global(.qqq-pred-card__rec) {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        :global(.qqq-pred-card__rec-shape) {
          font-family: ui-monospace, monospace;
          font-size: 13px;
          color: #ccc;
        }
        :global(.qqq-pred-card__dir-label) {
          font-size: 10px;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        :global(.qqq-pred-card__directional),
        :global(.qqq-pred-card__top3),
        :global(.qqq-pred-card__reasoning) {
          margin-bottom: 16px;
        }
        :global(.qqq-pred-card__dir-row),
        :global(.qqq-pred-card__top3-row) {
          display: grid;
          grid-template-columns: 100px 1fr 50px;
          gap: 8px;
          align-items: center;
          font-family: ui-monospace, monospace;
          font-size: 12px;
          padding: 3px 0;
        }
        :global(.qqq-pred-card__dir-bar),
        :global(.qqq-pred-card__top3-bar) {
          height: 8px;
          background: rgba(255,255,255,0.05);
          border-radius: 2px;
          overflow: hidden;
        }
        :global(.qqq-pred-card__dir-bar > div),
        :global(.qqq-pred-card__top3-bar > div) {
          height: 100%;
        }
        :global(.qqq-pred-card__reasoning p) {
          font-size: 12px;
          color: #aaa;
          line-height: 1.5;
          margin: 4px 0 0;
        }
        :global(.qqq-pred-card__details) {
          font-size: 12px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #1e1e2a;
        }
        :global(.qqq-pred-card__details summary) {
          cursor: pointer;
          color: #888;
          font-size: 11px;
          margin-bottom: 8px;
        }
        :global(.qqq-pred-card__details ul) {
          margin: 8px 0;
          padding-left: 16px;
          color: #aaa;
        }
        :global(.qqq-pred-card__details li) {
          margin: 2px 0;
        }
        :global(.qqq-pred-card__loading),
        :global(.qqq-pred-card__error),
        :global(.qqq-pred-card__empty) {
          padding: 24px;
          text-align: center;
          color: #888;
          font-size: 13px;
        }
        :global(.qqq-pred-card__run) {
          background: transparent;
          color: #c8a96e;
          border: 1px solid #c8a96e66;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          font-family: -apple-system, sans-serif;
        }
        :global(.qqq-pred-card__run:hover) {
          background: #c8a96e11;
        }
        .qqq-pred-rules {
          background: #0c0c11;
          border: 1px solid #1e1e2a;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }
        .qqq-pred-rules h3 {
          font-size: 11px;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 12px;
        }
        .qqq-pred-rules ul {
          margin: 0;
          padding-left: 0;
          list-style: none;
          font-family: ui-monospace, monospace;
          font-size: 12px;
        }
        .qqq-pred-rules li {
          padding: 4px 0;
          border-bottom: 1px solid #15151e;
        }
        .qqq-pred-rules li:last-child { border-bottom: none; }
        .qqq-pred-rules strong { color: #c8a96e; }
      `}</style>

      <div className="qqq-pred-header">
        <h2>Prediction Simulator</h2>
        <span className="qqq-pred-header__subtitle">P2-BIASED · live intraday model · validated N=200</span>
        <input
          type="date"
          className="qqq-pred-date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <HybridPanel />

      <F15Panel />

      <div className="qqq-pred-grid">
        <PredictionCard
          checkpoint="11:00"
          result={results['11:00']}
          loading={loading['11:00']}
          onRun={() => runPrediction('11:00')}
        />
        <PredictionCard
          checkpoint="11:30"
          result={results['11:30']}
          loading={loading['11:30']}
          onRun={() => runPrediction('11:30')}
        />
      </div>

      <div className="qqq-pred-rules">
        <h3>Decision filter (backtest-derived thresholds)</h3>
        <ul>
          <li><strong>TRADE</strong> &nbsp;&nbsp;&nbsp; — top-1 confidence ≥ 50% (11:30) or 45% (11:00) · backtest hit 85% / 89%</li>
          <li><strong>TRADE-DIRECTIONAL</strong> — top-1 weak but directional confidence ≥ 55% · backtest ~80% directional</li>
          <li><strong>SMALL</strong> &nbsp;&nbsp;&nbsp; — top-1 confidence 40-50% · backtest 65% · partial-conviction setups only</li>
          <li><strong>SKIP</strong> &nbsp;&nbsp;&nbsp;&nbsp; — top-1 confidence &lt; 40% · noise · default per system-player framework</li>
        </ul>
      </div>

      <div className="qqq-pred-rules">
        <h3>Architecture findings (what we know works)</h3>
        <ul>
          <li>✓ P2-BIASED (single LLM, 8 internal personas) beats P3 multi-call</li>
          <li>✓ Intraday vars by 11:30 = production baseline (57% top-1, 83.5% top-3)</li>
          <li>✓ Confidence-conditional: ≥50% top-1 → 85% hit rate (23% of days)</li>
          <li>✓ Directional 2-way: 89% hit on directional days</li>
          <li>✓ Pattern C trend days highly predictable (81% top-1, 96% top-3)</li>
          <li>✗ Daily-chart context HURTS (-4 to -8 pts) — don't add</li>
          <li>✗ Pre-market variables HURT (-5 pts)</li>
          <li>✗ 10:00 checkpoint UNUSABLE (-4.5 pts vs pre-market — partial pullback)</li>
        </ul>
      </div>
    </div>
  );
}
